import Phaser from "phaser";
import { config } from "@/config";
import { ensureAvatarAtlas } from "@/world/objects/AvatarAtlas";
import { ensureMapTextures } from "@/world/map/MapBuilder";
import { ensureTopDownTextures } from "@/world/map/textures/TopDownTextures";
import { ensureObliqueTextures } from "@/world/map/textures/ObliqueTextures";
import { getZoneManifest } from "@/world/map/ZoneManifest";
import { sceneKeyFor } from "@/scenes/routing";

export class BootScene extends Phaser.Scene {
  static readonly KEY = "BootScene";

  constructor() {
    super(BootScene.KEY);
  }

  preload(): void {
    this.load.json("zone-manifest", "/manifests/zones.json");
  }

  create(): void {
    ensureAvatarAtlas(this);
    ensureMapTextures(this);
    ensureTopDownTextures(this);
    ensureObliqueTextures(this);

    const manifest = getZoneManifest(this);
    const seenTilesets = new Set<string>();
    let pending = 0;

    for (const zone of Object.values(manifest)) {
      if (zone.type !== "tiled") continue;
      if (zone.tmj) {
        this.load.json("tmj:" + zone.tmj, zone.tmj);
        pending++;
      }
      if (zone.collision) {
        this.load.json("collision:" + zone.collision, zone.collision);
        pending++;
      }
      // Skip URL-based tileset loading when zone uses a pre-registered key
      // (e.g. procedurally-generated top-down tileset).
      if (zone.tilesetKey) continue;

      const projection = zone.projection ?? "iso";
      // Iso TMJ tiles are 64×32 diamonds; top-down/oblique tiles are square.
      const frameWidth = projection === "iso" ? 64 : 32;
      const frameHeight = projection === "iso" ? 32 : 32;
      for (const url of zone.tilesets ?? []) {
        if (seenTilesets.has(url)) continue;
        seenTilesets.add(url);
        this.load.spritesheet("tileset:" + url, url, { frameWidth, frameHeight });
        pending++;
      }
    }

    const startScene = () => this.scene.start(sceneKeyFor(this, config.zoneId));
    if (pending > 0) {
      this.load.once("complete", startScene);
      this.load.start();
    } else {
      startScene();
    }
  }
}
